import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Code, ConnectError } from '@connectrpc/connect';
import { SpecraButton, SpecraCard, SpecraCardContent, SpecraEmptyState, SpecraOverlayDrawer, SpecraPanelHeader, SpecraSelect, SpecraText } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { projectDataClient } from '../../../lib/connect/clients';
import { useCompanyProfiles } from '../../Delivery/Programs/referenceDataHooks';
import { useCompetitiveField } from '../hooks';
import { pursuitSectionPath } from '../record/pursuitSections';
import { RowActionsMenu } from '../record/RowActionsMenu';
import { BlackHatEditor, BlackHatFacts } from './BlackHatEditor';
const denied = (error)=>!!error && [
        Code.PermissionDenied,
        Code.Unauthenticated
    ].includes(ConnectError.from(error).code);
export function BlackHatPanel({ pursuitId, canWrite, active = true }) {
    const cache = useQueryClient();
    const location = useLocation();
    const requested = new URLSearchParams(location.search).get('blackHatParticipant') || '';
    const [participantId, setParticipantId] = useState(requested);
    const [showHistory, setShowHistory] = useState(false);
    const [refused, setRefused] = useState(false);
    const [editorOpen, setEditorOpen] = useState(false);
    const field = useCompetitiveField(pursuitId);
    const companies = useCompanyProfiles();
    const queryKey = [
        'black-hat',
        pursuitId
    ];
    const list = useQuery({
        queryKey,
        queryFn: ()=>projectDataClient.listBlackHatAssessments({
                pursuitId
            }),
        retry: false
    });
    const history = useQuery({
        queryKey: [
            'black-hat-history',
            pursuitId,
            participantId
        ],
        queryFn: ()=>projectDataClient.getBlackHatAssessmentHistory({
                pursuitId,
                participantId
            }),
        enabled: showHistory && !!participantId,
        retry: false
    });
    useEffect(()=>{
        if (requested) setParticipantId(requested);
    }, [
        requested
    ]);
    useEffect(()=>{
        if (active) {
            void cache.invalidateQueries({
                queryKey: [
                    'black-hat',
                    pursuitId
                ]
            });
            void cache.invalidateQueries({
                queryKey: [
                    'competitive-field',
                    pursuitId
                ]
            });
        }
    }, [
        active,
        cache,
        pursuitId
    ]);
    const entries = field.data?.entries ?? [];
    const entry = entries.find((e)=>e.participant?.id === participantId);
    const head = list.data?.assessments.find((a)=>a.participantId === participantId);
    const ids = [
        ...new Set([
            ...entries.flatMap((e)=>e.participant ? [
                    e.participant.id
                ] : []),
            ...list.data?.assessments.map((a)=>a.participantId) ?? []
        ])
    ];
    const nameOf = (id)=>{
        const companyId = entries.find((e)=>e.participant?.id === id)?.participant?.companyProfileId ?? list.data?.assessments.find((a)=>a.participantId === id)?.companyProfileId;
        return companies.data?.find((c)=>c.id === companyId)?.name || `Unavailable competitor (${id})`;
    };
    function saved(assessment) {
        cache.setQueryData(queryKey, (old)=>({
                assessments: [
                    ...(old?.assessments ?? []).filter((a)=>a.participantId !== assessment.participantId),
                    assessment
                ]
            }));
        void cache.invalidateQueries({
            queryKey: [
                'black-hat-history',
                pursuitId,
                participantId
            ]
        });
        void cache.invalidateQueries({
            queryKey: [
                'capture-output-lifecycle',
                pursuitId
            ]
        });
        void cache.invalidateQueries({
            queryKey: [
                'capture-outputs',
                pursuitId
            ]
        });
    }
    if (refused || denied(list.error) || denied(field.error) || denied(history.error)) return /*#__PURE__*/ _jsx(SpecraText, {
        as: "p",
        role: "alert",
        "data-testid": Testids.blackHat.error,
        children: "Black Hat access denied. Read and sensitive Pursuit access are required."
    });
    return /*#__PURE__*/ _jsxs(SpecraCard, {
        className: "min-w-0",
        "data-testid": Testids.blackHat.panel,
        children: [
            /*#__PURE__*/ _jsx(SpecraPanelHeader, {
                title: "Black Hat · qualitative assessment"
            }),
            /*#__PURE__*/ _jsxs(SpecraCardContent, {
                className: "space-y-3",
                children: [
                    /*#__PURE__*/ _jsx(SpecraText, {
                        as: "p",
                        "data-testid": Testids.blackHat.pricing,
                        children: "Pricing: UNKNOWN"
                    }),
                    /*#__PURE__*/ _jsx(SpecraText, {
                        as: "p",
                        className: "[overflow-wrap:anywhere]",
                        children: "Competitor facts, hypotheses and evidence are maintained in Competitors. Assessments retain the evidence as saved with each version."
                    }),
                    /*#__PURE__*/ _jsx(SpecraButton, {
                        asChild: true,
                        variant: "secondary",
                        "data-testid": Testids.blackHat.competitionLink,
                        children: /*#__PURE__*/ _jsx(Link, {
                            to: pursuitSectionPath(pursuitId, 'competitors'),
                            children: "Open Competitors"
                        })
                    }),
                    list.isPending || field.isPending || companies.isPending ? /*#__PURE__*/ _jsx("output", {
                        "data-testid": Testids.blackHat.loading,
                        children: "Loading Black Hat competitors…"
                    }) : list.isError || field.isError || companies.isError ? /*#__PURE__*/ _jsxs("div", {
                        role: "alert",
                        "data-testid": Testids.blackHat.error,
                        children: [
                            "Black Hat competitors could not be loaded.",
                            /*#__PURE__*/ _jsx(SpecraButton, {
                                "data-testid": Testids.blackHat.retry,
                                onClick: ()=>{
                                    void list.refetch();
                                    void field.refetch();
                                    void companies.refetch();
                                },
                                children: "Retry Black Hat"
                            })
                        ]
                    }) : /*#__PURE__*/ _jsxs(_Fragment, {
                        children: [
                            !ids.length && /*#__PURE__*/ _jsx(SpecraEmptyState, {
                                size: "compact",
                                title: "No competitors yet.",
                                description: "Add a competitor in Competitors first.",
                                "data-testid": Testids.pursuitBlackHat.emptyState
                            }),
                            !!ids.length && /*#__PURE__*/ _jsxs(SpecraSelect, {
                                "aria-label": "Black Hat competitor",
                                "data-testid": Testids.blackHat.competitor,
                                value: participantId,
                                onChange: (event)=>{
                                    setParticipantId(event.target.value);
                                    setShowHistory(false);
                                },
                                children: [
                                    /*#__PURE__*/ _jsx("option", {
                                        value: "",
                                        children: "Choose a competitor"
                                    }),
                                    ids.map((id)=>/*#__PURE__*/ _jsxs("option", {
                                            value: id,
                                            children: [
                                                nameOf(id),
                                                !entries.some((e)=>e.participant?.id === id) ? ' · removed participant' : ''
                                            ]
                                        }, id)),
                                    participantId && !ids.includes(participantId) && /*#__PURE__*/ _jsx("option", {
                                        value: participantId,
                                        children: "Unavailable competitor"
                                    })
                                ]
                            }),
                            participantId && /*#__PURE__*/ _jsxs(_Fragment, {
                                children: [
                                    !entry && /*#__PURE__*/ _jsx(SpecraText, {
                                        as: "p",
                                        className: "[overflow-wrap:anywhere]",
                                        children: "Competitor participant is missing or deleted. Saved versions remain read-only where available."
                                    }),
                                    !showHistory && !editorOpen && (head ? /*#__PURE__*/ _jsx(BlackHatFacts, {
                                        assessment: head
                                    }) : /*#__PURE__*/ _jsx(SpecraEmptyState, {
                                        size: "compact",
                                        title: "No saved assessment.",
                                        description: entry && canWrite ? 'Add the first Black Hat assessment for this competitor.' : 'You need edit access to add one.',
                                        "data-testid": Testids.pursuitBlackHat.emptyState,
                                        action: entry && canWrite ? /*#__PURE__*/ _jsx(SpecraButton, {
                                            size: "sm",
                                            onClick: ()=>setEditorOpen(true),
                                            "data-testid": Testids.pursuitBlackHat.addButton,
                                            children: "Add black hat entry"
                                        }) : undefined
                                    })),
                                    !showHistory && !editorOpen && head && entry && canWrite && /*#__PURE__*/ _jsx("div", {
                                        className: "flex justify-end",
                                        children: /*#__PURE__*/ _jsx(RowActionsMenu, {
                                            rowName: "Black Hat assessment",
                                            onEdit: {
                                                label: 'Edit assessment',
                                                testId: Testids.blackHat.edit,
                                                onSelect: ()=>setEditorOpen(true)
                                            }
                                        })
                                    }),
                                    entry && canWrite && /*#__PURE__*/ _jsx(SpecraOverlayDrawer, {
                                        open: editorOpen && !showHistory,
                                        onOpenChange: setEditorOpen,
                                        title: "Black Hat assessment",
                                        width: "lg",
                                        testId: Testids.pursuitBlackHat.dialog,
                                        children: /*#__PURE__*/ _jsx(BlackHatEditor, {
                                            pursuitId: pursuitId,
                                            participantId: participantId,
                                            head: head,
                                            evidence: entry.evidence,
                                            onSaved: saved,
                                            onDenied: ()=>setRefused(true)
                                        }, participantId)
                                    }),
                                    !canWrite && /*#__PURE__*/ _jsx(SpecraText, {
                                        as: "p",
                                        className: "[overflow-wrap:anywhere]",
                                        children: "Read-only: update and project write permissions are required to save."
                                    }),
                                    /*#__PURE__*/ _jsx(SpecraButton, {
                                        variant: "secondary",
                                        "data-testid": Testids.blackHat.history,
                                        onClick: ()=>{
                                            setEditorOpen(false);
                                            setShowHistory(true);
                                        },
                                        children: "Version history"
                                    }),
                                    showHistory && /*#__PURE__*/ _jsxs("div", {
                                        className: "space-y-3",
                                        children: [
                                            /*#__PURE__*/ _jsx(SpecraButton, {
                                                variant: "ghost",
                                                "data-testid": Testids.blackHat.closeHistory,
                                                onClick: ()=>setShowHistory(false),
                                                children: "Back to current assessment"
                                            }),
                                            history.isPending && /*#__PURE__*/ _jsx("output", {
                                                children: "Loading Black Hat history…"
                                            }),
                                            history.isError && /*#__PURE__*/ _jsxs("div", {
                                                role: "alert",
                                                children: [
                                                    ConnectError.from(history.error).code === Code.NotFound ? 'No version history available for this participant.' : 'History could not be loaded.',
                                                    /*#__PURE__*/ _jsx(SpecraButton, {
                                                        onClick: ()=>void history.refetch(),
                                                        children: "Retry history"
                                                    })
                                                ]
                                            }),
                                            history.data?.versions.map((version)=>/*#__PURE__*/ _jsxs("article", {
                                                    "data-testid": Testids.blackHat.version,
                                                    className: "space-y-2 rounded-md border p-3",
                                                    children: [
                                                        /*#__PURE__*/ _jsx(SpecraText, {
                                                            as: "p",
                                                            className: "[overflow-wrap:anywhere]",
                                                            children: "Read-only saved version"
                                                        }),
                                                        /*#__PURE__*/ _jsx(BlackHatFacts, {
                                                            assessment: version
                                                        })
                                                    ]
                                                }, version.id))
                                        ]
                                    })
                                ]
                            })
                        ]
                    })
                ]
            })
        ]
    });
}
